import { fetchJson, rawFetchJson } from './api';
import type { ServiceProfile } from '@/types';

export interface Capabilities {
  figmaPush: boolean;
  designTokenExtraction: boolean;
  unblockedEnrichment: boolean;
  stitchRedesign: boolean;
  geminiVision: boolean;
  linearIntegration: boolean;
}

export async function fetchCapabilities(): Promise<Capabilities> {
  return rawFetchJson<Capabilities>('/capabilities');
}

export async function fetchServiceProfiles(): Promise<ServiceProfile[]> {
  return fetchJson('/services');
}

export async function fetchServiceProfile(id: string): Promise<ServiceProfile> {
  return fetchJson(`/services/${id}`);
}

export async function createServiceProfile(data: {
  repo: string;
  displayName: string;
  description?: string;
  previewUrl?: string;
  baseBranch?: string;
  auth?: {
    type: 'cookie' | 'bearer' | 'firebase_token' | 'none';
    loginPath?: string;
    credentials?: Record<string, string>;
    extraFields?: Record<string, string>;
  };
  techStack?: {
    framework?: string;
    uiLibrary?: string;
    designSystem?: string;
    designSystemFigmaKey?: string;
  };
  userPersonas?: Array<{ name: string; description?: string; primaryFlows?: string[] }>;
  designGuidelines?: string;
}): Promise<ServiceProfile> {
  return fetchJson('/services', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateServiceProfile(
  id: string,
  data: Record<string, unknown>,
): Promise<ServiceProfile> {
  return fetchJson(`/services/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteServiceProfile(id: string): Promise<ServiceProfile> {
  return fetchJson(`/services/${id}`, {
    method: 'DELETE',
  });
}

export async function testServiceAuth(id: string): Promise<{ success: boolean; message: string }> {
  return fetchJson(`/services/${id}/test-auth`, {
    method: 'POST',
  });
}

export async function refreshServiceContext(id: string, force?: boolean): Promise<{ message: string; status: string }> {
  const params = force ? '?force=true' : '';
  return fetchJson(`/services/${id}/refresh-context${params}`, {
    method: 'POST',
  });
}

export async function fetchDesignSystemTemplate(): Promise<{ content: string }> {
  return fetchJson('/services/design-system-template');
}

export async function extractDesignTokens(id: string): Promise<{ message: string; tokenLength: number }> {
  return fetchJson(`/services/${id}/extract-design-tokens`, {
    method: 'POST',
  });
}

// --- Auth Strategies ---

export async function addAuthStrategy(id: string, data: {
  name: string;
  description?: string;
  type: 'cookie' | 'bearer' | 'firebase_token' | 'none';
  loginPath?: string;
  credentials?: Record<string, string>;
  extraFields?: Record<string, string>;
  isDefault?: boolean;
}): Promise<ServiceProfile> {
  return fetchJson(`/services/${id}/auth-strategies`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAuthStrategy(id: string, strategyName: string, data: {
  description?: string;
  type?: 'cookie' | 'bearer' | 'firebase_token' | 'none';
  loginPath?: string;
  credentials?: Record<string, string>;
  extraFields?: Record<string, string>;
  isDefault?: boolean;
}): Promise<ServiceProfile> {
  return fetchJson(`/services/${id}/auth-strategies/${encodeURIComponent(strategyName)}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteAuthStrategy(id: string, strategyName: string): Promise<ServiceProfile> {
  return fetchJson(`/services/${id}/auth-strategies/${encodeURIComponent(strategyName)}`, {
    method: 'DELETE',
  });
}
